import {_} from 'lodash';
import * as ko from "knockout";
import {IMessageData, IResponse, ISystem, ISystemRole} from "../../../index";
import {KOSubscriptionTracker} from "../../../util/KOSubscriptionTracker";
import {IDialogOptions} from "../../dialog/index";
import PromiseRequest from "../../../req/PromiseRequest";
import {nsRuleset} from "../../ruleset/nsRuleset";
import {getSearchObjectParams} from "../../../util/request";
import {ObjectTypeId, SPINNER} from "../../../constants";
import {DELETING_MESSAGE, PLEASE_WAIT_MESSAGE} from "../../../constants/Language";
import {ButtonManager} from "../../../util/ButtonManager";
import {createErrorMessage, isPartOf} from "../../../util/misc";
import {nsRoute} from "../../../util/route";
import {global} from "../../../appContext";
import {getCancelButton, getSubmitButton} from "../../../constants/RouteDefinitions";
import ButtonOrder = nsRoute.ButtonOrder;
import purge_collectionHtml from './purge-collection.html?raw';

export const template = purge_collectionHtml;

interface IParams {
    dialog: IDialogOptions
    system: ISystem
}

type TStep = 'init' | 'introduction' | 'process' | 'success' | 'error'

export class viewModel {
    private readonly isReady: KnockoutObservable<boolean | null> = ko.observable(null);
    private readonly kosub: KOSubscriptionTracker = new KOSubscriptionTracker();
    private readonly messageData: KnockoutObservable<IMessageData | null> = ko.observable(null);

    private readonly req_searchobject_A: PromiseRequest = new PromiseRequest('searchobject')
    private readonly req_searchobject_B: PromiseRequest = new PromiseRequest('searchobject')

    private readonly pageButtons: ButtonManager = new ButtonManager()

    private readonly step: KnockoutObservable<TStep | null> = ko.observable(null);
    private readonly toggleOverride: KnockoutObservable<boolean> = ko.observable(true);

    private numActiveUsers!: number;
    private numInactiveUsers!: number;
    private numTotalUsers!: number;

    private requests!: PromiseRequest[]

    constructor(private params: IParams) {
        this.kosub.subscribe(this.step, this.onStep.bind(this))

        this.pageButtons.add(getCancelButton(this.params.dialog.doClose, {
            enabled: () => !this.isStep('process'),
            validator: () => !this.isStep('success', 'error'),
        }))

        this.pageButtons.add(getSubmitButton({
            onClick: () => this.step('process'),
            cssClass: 'danger',
            enabled: () => this.isReady(),
            icon: 'fa-arrow-right',
            iconPlacement: 'right',
            label: 'yes, continue',
            validator: () => this.isStep('introduction'),
        }))

        this.pageButtons.add(getCancelButton(this.params.dialog.doClose, {
            enabled: () => this.isReady(),
            icon: 'fa-times',
            label: 'close',
            order: ButtonOrder.CONTINUE,
            validator: () => this.isStep('success', 'error'),
        }))

        this.step('init')
    }

    private onStep() {
        switch (this.step()) {
            case "init": {
                this.isReady(false)
                this.messageData({message: PLEASE_WAIT_MESSAGE})

                const ruleset = nsRuleset.generateRuleset({
                    rules: [
                        nsRuleset.generateRule({
                            argument: '',
                            argumentoperator: 'Har verdi',
                            attribute: `[OBJECTPERMISSION.SYSTEM;Name;Id;${this.params.system.id}]`,
                        }),
                    ]
                })

                const searchObjectParams = getSearchObjectParams({
                    objecttypeid: ObjectTypeId.User,
                    jruleset: JSON.stringify(ruleset),
                });

                Promise.resolve()
                    .then(() => this.req_searchobject_A
                        .request(_.defaultsDeep({deleted: 1}, searchObjectParams), true)
                        .then((data: IResponse) => {
                            this.numActiveUsers = data.totalresults
                        })
                    )
                    .then(() => this.req_searchobject_B
                        .request(_.defaultsDeep({deleted: -1}, searchObjectParams), true)
                        .then((data: IResponse) => {
                            this.numInactiveUsers = data.totalresults
                        })
                    )
                    .then(() => {
                        this.numTotalUsers = this.numActiveUsers + this.numInactiveUsers
                        this.messageData(null)
                        this.isReady(true)
                        this.step("introduction")
                    })
                    .catch((reason: string) => {
                        this.step('error')
                        this.messageData(createErrorMessage(String(reason)))
                        this.params.dialog.onError?.()
                    })

                break
            }
            case 'process': {
                this.params.dialog.isClosable?.(false)
                this.isReady(false)
                this.messageData({message: DELETING_MESSAGE})

                this.requests?.forEach(o => o.abort())
                this.requests = []

                const fnArr: (() => Promise<any>)[] = this.params.system.roles.map((permission: ISystemRole) => {
                    return () => {
                        const req_purgesystemrolepermissions = new PromiseRequest('purgesystemrolepermissions')
                        const req_deleterole = new PromiseRequest('deleterole')
                        this.requests.push(req_purgesystemrolepermissions)
                        this.requests.push(req_deleterole)
                        return Promise.resolve()

                            // --- Start up ----------------------------------------------------------------------------
                            .then(() => this.messageData({message: `((((deleting)) ((permission|ac)) [${permission.name}]|td)) ${SPINNER}`}))

                            // --- Purge role --------------------------------------------------------------------------
                            .then(() => req_purgesystemrolepermissions
                                .request({
                                    systemroleid: permission.id,
                                    includeoverrides: this.toggleOverride(),
                                }))

                            // --- Delete role -------------------------------------------------------------------------
                            .then(() => req_deleterole.request({id: permission.id}))

                    }
                })

                const doNext = (): Promise<any> => {
                    if (!fnArr.length) {
                        return Promise.resolve()
                    }
                    const fn = fnArr.shift()
                    return fn!()
                        .then(() => {
                            if (fnArr.length) {
                                return doNext()
                            }
                        })
                }
                doNext()

                    // --- Delete system -------------------------------------------------------------------------------
                    .then(() => {
                        this.messageData({message: DELETING_MESSAGE})
                        const req_deletesystem = new PromiseRequest('deletesystem')
                        this.requests.push(req_deletesystem)
                        return req_deletesystem.request({id: this.params.system.id})
                    })

                    // --- Finish --------------------------------------------------------------------------------------
                    .then(() => {
                        this.messageData(null)
                        this.isReady(true)
                        this.step("success")
                    })

                    // --- Error handling ------------------------------------------------------------------------------
                    .catch((reason: string) => {
                        this.step('error')
                        this.messageData(createErrorMessage(String(reason)))
                        this.params.dialog.onError?.()
                    })

                break
            }
            case 'success': {
                global.cacheManager.removeCache('searchsystem')
                global.cacheManager.removeCache('searchobject')
                global.cacheManager.removeCache('getobject')
                this.params.dialog.isClosable?.(true)
                this.messageData(null)
                this.isReady(true)

                this.params.dialog.onSuccess?.()

                break
            }
            case 'error': {
                this.params.dialog.isClosable?.(true)
                this.isReady(true)

                break
            }
        }
    }

    private isStep(...steps: TStep[]): boolean {
        const step = this.step();
        return step != null && isPartOf(step, ...steps)
    }

    dispose() {
        this.kosub.unsuscribeAll();
        this.requests?.forEach(o => o.abort())
    }
}
